import React from 'react';

import { LoadingButton } from '@mui/lab';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { KeyboardArrowLeft } from '@mui/icons-material';
import LoadingScreen from 'components/LoadingScreen';
import { Link } from 'react-router-dom';

// ----------------------------------------------------------------- styles

const SpinnerBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
}));

const CustomLoadingButton = styled(LoadingButton)(({ theme }) => ({
  padding: '10px 35px',
  '&.Mui-disabled': {
    color: theme.palette.common.white,
  },
  '&.MuiLoadingButton-loading': {
    color: 'transparent !important',
  },
}));

const CustomBackButton = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  spacing: '8px',
  alignItems: 'center',
  cursor: 'pointer',
  color: theme.palette.neutral100.main,
}));

const Image = styled('img')(() => ({}));

// ----------------------------------------------------------------- components

export const Title = ({ children }) => {
  return (
    <Typography
      typography={{ xs: 'xSubheading18', sm: 'xSubheading28' }}
      color={{ xs: 'common.black', sm: 'grey.1' }}
      margin={{ xs: '0 16px 16px', sm: '32px 0' }}
    >
      {children}
    </Typography>
  );
};

export const PageHeader = ({
  title,
  breadcrumbs,
  tabs,
  actionButton,
  spacing,
  badge,
  children,
  isJobPosting,
  ...props
}) => {
  return (
    <Stack
      spacing={spacing || { xs: 4, md: 5 }}
      mb={{ xs: isJobPosting ? 1 : 3, md: isJobPosting ? 2 : 4 }}
      {...props}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={{ xs: 3, md: 'unset' }}
      >
        <Stack
          spacing={2}
          sx={{
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            '::-webkit-scrollbar': { display: 'none' },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
          }}
        >
          <Stack direction="row" id="customBreadcrumbs" alignItems="center">
            {React.Children.toArray(
              breadcrumbs.map(({ header, isSelected, url }, index) => {
                if (!header) {
                  return null;
                }

                let textColor;

                if (isSelected) {
                  textColor = 'primaryBrandPurwadhika.main';
                } else {
                  textColor = 'neutral400.main';
                }

                const breadcrumb =
                  url && !isSelected ? (
                    <Link to={url} style={{ textDecoration: 'none' }}>
                      <Typography
                        variant={
                          isSelected ? 'subheading4medium' : 'paragraph2'
                        }
                        color={textColor}
                      >
                        {header}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography
                      variant={isSelected ? 'subheading4medium' : 'paragraph2'}
                      color={textColor}
                    >
                      {header}
                    </Typography>
                  );

                const separatorColor =
                  index === breadcrumbs.length - 2
                    ? 'primaryBrandPurwadhika.main'
                    : 'neutral400.main';

                const separatorVariant =
                  index === breadcrumbs.length - 2
                    ? 'subheading4medium'
                    : 'paragraph2';

                return (
                  <React.Fragment key={header}>
                    {breadcrumb}
                    {index < breadcrumbs.length - 1 && (
                      <Typography
                        variant={separatorVariant}
                        color={separatorColor}
                        sx={{ mx: 1 }}
                      >
                        /
                      </Typography>
                    )}
                  </React.Fragment>
                );
              }),
            )}
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography
              typography={{ md: 'h4', xs: 'subheading1' }}
              color="neutral100.main"
            >
              {title}
            </Typography>
            {Boolean(badge) && badge}
          </Stack>
        </Stack>
        {Boolean(actionButton) && actionButton}
      </Stack>
      {tabs}
    </Stack>
  );
};

export const CustomButton = ({
  variant,
  loading,
  disabled,
  onClick,
  label,
  fullWidth,
  ...other
}) => (
  <CustomLoadingButton
    variant={variant || 'contained'}
    loading={loading}
    loadingIndicator={
      <CircularProgress sx={{ color: '#FFFFFF' }} size={24} thickness={6} />
    }
    onClick={onClick}
    disabled={disabled}
    fullWidth={fullWidth}
    {...other}
  >
    {label}
  </CustomLoadingButton>
);

export const Loader = ({ ...other }) => (
  <SpinnerBox {...other}>
    <LoadingScreen />
  </SpinnerBox>
);

export const EmptyScreen = ({
  imageSrc = '',
  imageStyle = {},
  title = '',
  subtitle = '',
  actionButton,
}) => (
  <Stack alignItems="center" py={3} textAlign="center">
    <Image
      src={imageSrc || '/dashboard/static/images/paper-plane.png'}
      alt="image"
      sx={{ width: { xs: '168px', md: '224px' }, ...imageStyle }}
    />

    <Typography
      typography={{ xs: 'subheading3', md: 'h4' }}
      mb={{ xs: 0.5, md: 2 }}
    >
      {title}
    </Typography>

    <Typography
      typography={{ xs: 'paragraph2', md: 'paragraph' }}
      maxWidth={{ md: '521px' }}
    >
      {subtitle}
    </Typography>

    {Boolean(actionButton) && <Box mt={2}>{actionButton}</Box>}
  </Stack>
);

export const BackButton = ({ to }) => {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <CustomBackButton>
        <KeyboardArrowLeft
          sx={{
            fontSize: { xs: '16px', md: '24px' },
            mr: '6px',
          }}
        />
        <Typography
          typography={{ xs: 'caption1', md: 'subheading3medium' }}
          lineHeight={{ xs: '16px', md: '24px' }}
          sx={{ mb: '-1px' }}
        >
          Back
        </Typography>
      </CustomBackButton>
    </Link>
  );
};
